import { deleteToken, getMessaging, getToken, isSupported, type Messaging } from "firebase/messaging";
import { doc, deleteDoc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { app, db } from "./firebase";

/* ============================================================
   Notificações

   O token do aparelho fica em `users/{uid}/pushTokens/{id}`. Cada pessoa
   pode ter vários — celular, notebook — e o servidor dispara pra todos.
   Token que o Firebase recusa é apagado pela própria função.

   As preferências ficam em `users/{uid}.notify` e são conferidas NO
   SERVIDOR antes de enviar. Conferir no cliente não serviria de nada:
   quem manda é a função.

   O que este arquivo trata com cuidado é uma armadilha do app anterior:
   a tela dizia "notificações ligadas" quando o NAVEGADOR tinha dado
   permissão, e nada chegava porque o token nunca foi gravado no
   Firestore. Aqui o estado que a tela mostra é `salvo` — o registro no
   banco —, não a permissão.
   ============================================================ */

const VAPID = import.meta.env["VITE_FIREBASE_VAPID_KEY"] as string | undefined;

let messaging: Messaging | null = null;

/** A chave VAPID do projeto foi configurada? Sem ela nada funciona. */
export const temChave = (): boolean => !!VAPID && VAPID.length > 20;

export async function suportado(): Promise<boolean> {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export const permissao = (): NotificationPermission | "unsupported" =>
  "Notification" in window ? Notification.permission : "unsupported";

/** Um id estável por aparelho, pra não acumular token repetido. */
function aparelho(): string {
  const CHAVE = "nhomi:aparelho";
  try {
    let id = localStorage.getItem(CHAVE);
    if (!id) {
      id = (crypto.randomUUID?.() ?? String(Date.now() + Math.random())).slice(0, 24);
      localStorage.setItem(CHAVE, id);
    }
    return id;
  } catch {
    // Navegador com armazenamento bloqueado: o aparelho vira um novo a
    // cada sessão. Chato, mas melhor que não registrar nada.
    return `efemero-${Date.now().toString(36)}`;
  }
}

async function registroDoWorker(): Promise<ServiceWorkerRegistration> {
  // O worker é registrado à mão e não pelo `getToken`: assim ele é o
  // MESMO arquivo em todo lugar, e dá pra saber que existe antes de
  // pedir permissão pra pessoa.
  return navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" });
}

export type MotivoDaRecusa =
  | "sem-chave" | "sem-suporte" | "negada" | "sem-token" | "sem-login" | "regras" | "erro";

export interface Resultado { ok: boolean; motivo?: MotivoDaRecusa; detalhe?: string }

export const EXPLICACAO: Record<MotivoDaRecusa, string> = {
  "sem-chave": "As notificações ainda não foram configuradas no projeto (falta a chave VAPID).",
  "sem-suporte": "Este navegador não recebe notificações. No iPhone, só funciona com o app instalado na tela de início.",
  "negada": "Você negou as notificações pra este site. Dá pra reverter nas configurações do navegador.",
  "sem-token": "O navegador não devolveu um endereço de entrega. Tente de novo.",
  "sem-login": "Entre na sua conta primeiro.",
  "regras": "O banco recusou gravar o aparelho.",
  "erro": "Não consegui ligar as notificações.",
};

/** Pede permissão e registra ESTE aparelho. */
export async function ligar(uid: string): Promise<Resultado> {
  if (!temChave()) return { ok: false, motivo: "sem-chave" };
  if (!await suportado()) return { ok: false, motivo: "sem-suporte" };
  if (!uid) return { ok: false, motivo: "sem-login" };

  if (await Notification.requestPermission() !== "granted") {
    return { ok: false, motivo: "negada" };
  }

  try {
    const registration = await registroDoWorker();
    messaging ??= getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID, serviceWorkerRegistration: registration });
    if (!token) return { ok: false, motivo: "sem-token" };

    try {
      await setDoc(doc(db, "users", uid, "pushTokens", aparelho()), {
        token,
        platform: navigator.userAgent.slice(0, 120),
        standalone: matchMedia("(display-mode: standalone)").matches,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      // Falhar aqui em silêncio é o pior caso: a tela diria "ligadas" e
      // nada chegaria, porque o servidor não teria pra quem mandar.
      const codigo = (err as { code?: string }).code;
      return {
        ok: false,
        motivo: codigo === "permission-denied" ? "regras" : "erro",
        detalhe: (err as Error).message,
      };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, motivo: "erro", detalhe: (err as Error).message };
  }
}

/** Tira este aparelho da conta ao sair, sem mexer na permissão. Ver o
    mesmo nome em `nativo/src/lib/push.ts`. */
export async function esquecerEsteAparelho(uid: string): Promise<void> {
  await deleteDoc(doc(db, "users", uid, "pushTokens", aparelho())).catch(() => {});
}

/** Descadastra só este aparelho — os outros continuam recebendo. */
export async function desligar(uid: string): Promise<void> {
  try {
    if (messaging) await deleteToken(messaging).catch(() => {});
  } finally {
    await deleteDoc(doc(db, "users", uid, "pushTokens", aparelho())).catch(() => {});
  }
}

export interface Estado {
  suportado: boolean;
  comChave: boolean;
  permissao: NotificationPermission | "unsupported";
  /** O que importa: o aparelho está gravado no banco com o token atual. */
  salvo: boolean;
  erro: string;
}

/**
 * Estado real deste aparelho, passo a passo.
 *
 * `salvo` é o único que responde "vou receber?". Token trocado sem o
 * Firestore saber também conta como não registrado — é o caso que fazia
 * o app anterior mentir na tela.
 */
export async function estado(uid: string): Promise<Estado> {
  const r: Estado = {
    suportado: await suportado(),
    comChave: temChave(),
    permissao: permissao(),
    salvo: false,
    erro: "",
  };
  if (!r.suportado || !r.comChave || r.permissao !== "granted" || !uid) return r;

  try {
    const registration = await registroDoWorker();
    messaging ??= getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID, serviceWorkerRegistration: registration });
    if (token) {
      const snap = await getDoc(doc(db, "users", uid, "pushTokens", aparelho()));
      r.salvo = snap.exists() && (snap.data() as { token?: string }).token === token;
    }
  } catch (err) {
    r.erro = (err as { code?: string }).code ?? (err as Error).message;
  }
  return r;
}

/**
 * Reconserta o registro sozinho, sem incomodar ninguém.
 *
 * O servidor apaga tokens que o Firebase recusa, e já aconteceu de apagar
 * token bom por engano — aí ninguém recebia nada e não havia sinal na
 * tela. Como a permissão já está dada, dá pra regravar em silêncio.
 */
export async function garantirRegistro(uid: string): Promise<boolean> {
  if (permissao() !== "granted" || !uid) return false;
  const atual = await estado(uid);
  if (atual.salvo) return true;
  return (await ligar(uid)).ok;
}
